import { PersonAvatar } from "../shared/PersonAvatar.js";
import { nameInitials } from "../../app/hooks/inboxFormat.js";
import styles from "./Inbox.module.scss";

/**
 * A BUS chat's picture when nobody has given it one (ART-304). A bus chat has no show behind it, so there is no
 * flyer to fall back to, and its name ("Bus 1") reduces to nothing worth drawing. What a bus chat IS is the
 * people bunked on that coach, so the mark is their faces: two overlapping for a crew of two or more, the one
 * face for a crew of one, and the plain group mark only when nobody else is in it.
 *
 * The faces are the OTHER members (the viewer excluded), the same rule a DM row follows: your own face on
 * every chat you are in says nothing.
 */
export function CrewMark({
  personIds,
  avatars,
  names,
  fallback,
  shape = "circle",
}: {
  /** The chat's other members, in served order. */
  personIds: readonly string[];
  avatars: ReadonlyMap<string, string | null | undefined>;
  names: ReadonlyMap<string, string>;
  /** What to draw with nobody else in the chat. */
  fallback: string;
  /** The list row's mark is a circle; the header's is a rounded square. */
  shape?: "circle" | "rounded";
}) {
  const faces = personIds.slice(0, 2);
  if (faces.length === 0) {
    return (
      <span className={shape === "rounded" ? styles.chatMark : styles.avatar} aria-hidden="true">
        {fallback}
      </span>
    );
  }
  if (faces.length === 1) {
    const id = faces[0]!;
    return (
      <span aria-hidden="true" className={styles.crewMarkSingle}>
        <PersonAvatar
          personId={id}
          avatarMediaId={avatars.get(id) ?? null}
          initials={nameInitials(names.get(id) ?? "")}
          size="md"
        />
      </span>
    );
  }
  return (
    <span className={styles.crewMark} aria-hidden="true" data-testid="crew-mark">
      {faces.map((id) => (
        <PersonAvatar
          key={id}
          personId={id}
          avatarMediaId={avatars.get(id) ?? null}
          initials={nameInitials(names.get(id) ?? "")}
          size="xs"
          className={styles.crewMarkFace}
        />
      ))}
    </span>
  );
}
